import { useState, useCallback, useEffect } from "react";

/**
 * Configuration for edit mode hook
 * @template T The type of items being reordered
 */
export interface EditModeConfig<T> {
    /** Original items from store/API - acts as source of truth */
    initialItems: T[];
    /** Function to extract unique ID from item */
    getItemId: (item: T) => string;
    /** Async function to persist reordered items to backend */
    onSave: (orderedIds: string[]) => Promise<void>;
}

/**
 * Edit mode state
 * @template T The type of items being reordered
 */
export interface EditModeState<T> {
    /** Items in current order (may be reordered locally) */
    items: T[];
    /** Whether currently in edit mode */
    isEditMode: boolean;
    /** Whether API call is in progress */
    isSaving: boolean;
    /** Error from API call, if any */
    error: Error | null;
}

/**
 * Edit mode actions
 * @template T The type of items being reordered
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface EditModeActions<T> {
    /** Enter edit mode */
    enterEditMode: () => void;
    /** Exit edit mode and revert to original items */
    discardChanges: () => void;
    /** Persist reordered items to backend and exit edit mode */
    saveChanges: () => Promise<void>;
    /** Reorder items locally (moves item from one index to another) */
    reorderItems: (fromIndex: number, toIndex: number) => void;
    /** Clear error state (e.g., after showing error to user) */
    clearError: () => void;
}

/**
 * Hook for managing edit mode state and reordering logic
 *
 * Implements a workflow where:
 * 1. User enters edit mode
 * 2. User drags items to reorder (local state only, no API calls)
 * 3. User clicks Save (single API call with final order) or Discard (revert to original)
 *
 * @template T The type of items being reordered (must have unique ID)
 * @param config Configuration with initial items, ID extractor, and save handler
 * @returns [state, actions] tuple with current state and action handlers
 *
 * @example
 * const [state, actions] = useEditMode({
 *   initialItems: polls,
 *   getItemId: (poll) => poll._id,
 *   onSave: async (orderedIds) => {
 *     await dispatch(ChannelActions.reorderPolls({
 *       channelId: selectedChannel._id,
 *       orderedPollIds: orderedIds,
 *     }));
 *   },
 * });
 */
export const useEditMode = <T>(
    config: EditModeConfig<T>
): [EditModeState<T>, EditModeActions<T>] => {
    const [items, setItems] = useState<T[]>(config.initialItems);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Sync local items with initial items when not in edit mode
    // This ensures we always have fresh data from the store
    useEffect(() => {
        if (!isEditMode) {
            setItems(config.initialItems);
        }
    }, [config.initialItems, isEditMode]);

    const enterEditMode = useCallback(() => {
        setIsEditMode(true);
        setError(null);
    }, []);

    const discardChanges = useCallback(() => {
        setItems(config.initialItems);
        setIsEditMode(false);
        setError(null);
    }, [config.initialItems]);

    const saveChanges = useCallback(async () => {
        if (!isEditMode) return;

        setIsSaving(true);
        setError(null);

        try {
            const orderedIds = items.map(config.getItemId);
            await config.onSave(orderedIds);

            // Success: exit edit mode and keep the new order
            setIsEditMode(false);
            // Items stay as-is (newly persisted order)
        } catch (err) {
            // Error: keep edit mode active so user can retry/discard
            setError(
                err instanceof Error ? err : new Error("Unknown error occurred")
            );
        } finally {
            setIsSaving(false);
        }
    }, [isEditMode, items, config]);

    const reorderItems = useCallback((fromIndex: number, toIndex: number) => {
        setItems((current) => {
            // Validate indices
            if (
                fromIndex < 0 ||
                fromIndex >= current.length ||
                toIndex < 0 ||
                toIndex >= current.length
            ) {
                return current;
            }

            const newItems = [...current];
            const [removed] = newItems.splice(fromIndex, 1);
            newItems.splice(toIndex, 0, removed);
            return newItems;
        });
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const state: EditModeState<T> = {
        items,
        isEditMode,
        isSaving,
        error,
    };

    const actions: EditModeActions<T> = {
        enterEditMode,
        discardChanges,
        saveChanges,
        reorderItems,
        clearError,
    };

    return [state, actions];
};
